import { toast, useFocusReturn } from '@btsis/ui';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorKey } from '@/lib/api';
import { useRevealPseudonyms } from './queries';

/**
 * *Rumuzları Çöz*: the current six-digit code of the signed-in user's own
 * authenticator. Real names open for this session only; a recovery code is not
 * accepted and a code already used is refused — docs/decisions/0136.
 */
export function PseudonymRevealDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const focusReturn = useFocusReturn(open);
  const reveal = useRevealPseudonyms();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  function close() {
    setCode('');
    setError(null);
    onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await reveal.mutateAsync({ code });
      toast.success(t('pseudonym.dialog.revealed'));
      close();
    } catch (caught) {
      setError(errorKey(caught));
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth {...focusReturn}>
      <form onSubmit={submit} noValidate>
        <DialogTitle>{t('pseudonym.dialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('pseudonym.dialog.description')}
            </Typography>
            {error && <Alert severity="error">{t(error)}</Alert>}
            <TextField
              autoFocus
              fullWidth
              label={t('pseudonym.dialog.code')}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              slotProps={{
                htmlInput: { inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} color="inherit" disabled={reveal.isPending}>
            {t('common.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={reveal.isPending || code.length !== 6}
          >
            {reveal.isPending ? t('pseudonym.dialog.submitting') : t('pseudonym.dialog.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
